/**
 * Level-Stats-Utilities für die Karten-Level-Tabelle im Market-Dialog.
 * BCX-Tabellen stammen direkt aus der offiziellen Splinterlands-Settings-API
 * (https://api2.splinterlands.com/settings -> combine_rates / foundations_combine_rates / sk_combine_rates).
 * Index 0 = Common, 1 = Rare, 2 = Epic, 3 = Legendary. Jeder Wert ist die kumulierte
 * Anzahl BCX (Basiskarten), die für das jeweilige Level (Array-Index + 1) benötigt wird.
 */

// Editionen mit dem "modernen" (kürzeren) Level-Schema seit Rebellion
const MODERN_EDITIONS = [12, 14, 15, 16];
// Soulbound-Editionen (Gladius, Soulbound, ...) nutzen ein eigenes Schema
const SOULBOUND_EDITIONS = [6, 10, 13, 16, 18];

const COMBINE_RATES = [
  [1, 5, 14, 30, 60, 100, 150, 220, 300, 400],   // Common
  [1, 5, 14, 25, 40, 60, 85, 115],                // Rare
  [1, 4, 10, 20, 32, 46],                         // Epic
  [1, 3, 6, 11]                                   // Legendary
];

const FOUNDATIONS_COMBINE_RATES = [
  [1, 5, 14, 30, 60],   // Common
  [1, 5, 14, 25],       // Rare
  [1, 4, 10],           // Epic
  [1, 3]                // Legendary
];

const SK_COMBINE_RATES = [
  [1, 5, 13, 25, 41, 63, 89, 121, 159, 200],
  [0, 0, 1, 5, 11, 21, 33, 47, 63, 80],
  [0, 0, 0, 0, 1, 4, 8, 13, 20, 28],
  [0, 0, 0, 0, 0, 0, 1, 3, 6, 10]
];

const RARITY_TO_INDEX = { 1: 0, 2: 1, 3: 2, 4: 3 };

// Basis-CDN für die offiziellen Splinterlands-Fähigkeiten-Icons (gleiche CDN wie die Kartenbilder).
// Splinterlands pflegt zwei Ordner: "small" für die klassischen Fähigkeiten (Weaken, Snipe, ...)
// und den Ordner direkt darüber für neuere Fähigkeiten (Ambush, Charge, Echo, ...) - gegen die
// CDN geprüft: keine der beiden Varianten deckt allein alle Fähigkeiten ab.
const ABILITY_ICON_BASE = 'https://d36mxiodymuqjm.cloudfront.net/website/abilities';

/**
 * Leitet den Icon-Dateinamen-Slug aus einem Fähigkeitsnamen ab. "Kindred Spirit:Blast"/
 * "Kindred Will:X"-Fähigkeiten verweisen auf die Basis-Fähigkeit nach dem Doppelpunkt - dafür
 * gibt es kein eigenes Icon, nur für "Blast" selbst.
 */
const slugifyAbility = (abilityName) => {
  const base = (abilityName || '').includes(':') ? abilityName.split(':').pop() : abilityName;
  return (base || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

/**
 * Primäre Icon-URL (klassische Fähigkeiten) - im UI mit getAbilityIconUrlFallback und
 * einem Text-Fallback absichern, da nicht jede Fähigkeit unter diesem Pfad liegt.
 */
export const getAbilityIconUrl = (abilityName) => {
  const slug = slugifyAbility(abilityName);
  if (!slug) return '';
  return `${ABILITY_ICON_BASE}/small/ability_${slug}.png`;
};

/**
 * Zweite Icon-URL für neuere Fähigkeiten, die nur im Ordner ohne "small" liegen.
 */
export const getAbilityIconUrlFallback = (abilityName) => {
  const slug = slugifyAbility(abilityName);
  if (!slug) return '';
  return `${ABILITY_ICON_BASE}/ability_${slug}.png`;
};

/**
 * Wählt die passende Combine-Rate-Tabelle für eine Edition.
 * Best-effort-Zuordnung anhand der von Splinterlands selbst gepflegten
 * "modern_editions"/"soulbound_editions"-Listen; nicht für jede Edition 1:1 verifiziert.
 */
const getCombineRatesTable = (edition) => {
  if (SOULBOUND_EDITIONS.includes(edition)) return SK_COMBINE_RATES;
  if (MODERN_EDITIONS.includes(edition)) return FOUNDATIONS_COMBINE_RATES;
  return COMBINE_RATES;
};

/**
 * Liefert die kumulierte BCX-Anzahl pro Level für eine Karte (rarity 1-4, edition Zahl).
 * @returns {number[]|null} Array, Index 0 = Level 1, oder null wenn Rarität unbekannt/nicht levelbar
 */
export const getBcxPerLevel = (rarity, edition) => {
  const rarityIndex = RARITY_TO_INDEX[rarity];
  if (rarityIndex === undefined) return null;
  const table = getCombineRatesTable(edition);
  return table[rarityIndex] || null;
};

/**
 * Baut die Zeilen für die Level-Stats-Tabelle aus den rohen Splinterlands-Karten-Stats.
 * @param {Object} stats - details.stats aus der get_details-API (Arrays pro Attribut, Index 0 = Level 1)
 * @param {number} rarity
 * @param {number} edition
 * @returns {Array<Object>} Zeilen { level, cards, mana, attack, ranged, magic, armor, health, speed, abilities }
 */
export const buildLevelStatsRows = (stats, rarity, edition) => {
  if (!stats) return [];
  const bcxPerLevel = getBcxPerLevel(rarity, edition);
  // Max-Level bestimmt sich aus der BCX-Tabelle (die stats-Arrays sind auf 10 Einträge aufgefüllt,
  // auch wenn die Karte real früher ihr Maximum erreicht).
  const maxLevel = bcxPerLevel ? bcxPerLevel.length : (stats.mana?.length || 0);

  const rows = [];
  const abilitiesSoFar = [];
  for (let i = 0; i < maxLevel; i++) {
    // Fähigkeiten stacken: einmal freigeschaltet, bleiben sie auf allen höheren Leveln aktiv.
    // stats.abilities[i] enthält nur die NEU auf diesem Level dazukommenden Fähigkeiten.
    // Manche Karten liefern hier fälschlich einen String statt eines Arrays (API-Datenfehler) -
    // ein `for...of` über einen String würde ihn sonst in Einzelbuchstaben zerlegen.
    const abilitiesAtLevel = Array.isArray(stats.abilities?.[i]) ? stats.abilities[i] : [];
    for (const ability of abilitiesAtLevel) {
      if (typeof ability === 'string' && ability.length > 1 && !abilitiesSoFar.includes(ability)) {
        abilitiesSoFar.push(ability);
      }
    }
    rows.push({
      level: i + 1,
      cards: bcxPerLevel ? bcxPerLevel[i] : null,
      mana: stats.mana?.[i],
      attack: stats.attack?.[i],
      ranged: stats.ranged?.[i],
      magic: stats.magic?.[i],
      armor: stats.armor?.[i],
      health: stats.health?.[i],
      speed: stats.speed?.[i],
      newAbilities: abilitiesAtLevel.filter(a => typeof a === 'string' && a.length > 1),
      abilities: [...abilitiesSoFar]
    });
  }
  return rows;
};

/**
 * Liefert die (kumulierten) Werte für genau ein Level - für die Mini-Ansicht unter dem Kartenbild.
 * @returns {Object|null}
 */
export const getStatsForLevel = (stats, rarity, edition, level) => {
  const rows = buildLevelStatsRows(stats, rarity, edition);
  return rows.find(r => r.level === level) || rows[rows.length - 1] || null;
};
